import React, { ReactElement, useContext } from 'react'

import Heading from '../../Common/Components/Heading'
import P from '../../Common/Components/P'
import Fieldset from '../../Common/Components/Fieldset'
import Checkbox from '../../Common/Components/Checkbox'
import TextInput from '../../Common/Components/TextInput'
import FormRow from '../../Common/Components/FormRow'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ACDStateThresholds as ACDStateThresholdsLanguage } from '../../../../i18n'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
// CSS imports. Commented out because of current build process.
// import './ACDStateThresholds.scss'

/**
 * Component properties type.
 */
type Props = {
    isOnAvailableStateEnabled: boolean
    setIsOnAvailableStateEnabled: (enabled: boolean) => void
    onAvailableState: string
    setOnAvailableState: (newValue: string) => void
    isOnBusyStateEnabled: boolean
    setIsOnBusyStateEnabled: (enabled: boolean) => void
    onBusyState: string
    setOnBusyState: (newValue: string) => void
    isOnNAOrDNDStateEnabled: boolean
    setIsOnNAOrDNDStateEnabled: (enabled: boolean) => void
    onNAOrDNDState: string
    setOnNAOrDNDState: (newValue: string) => void
    isOnWrapupStateEnabled: boolean
    setIsOnWrapupStateEnabled: (enabled: boolean) => void
    onWrapupState: string
    setOnWrapupState: (newValue: string) => void
}

/**
 * ACD state thresholds section of Alarms tab in report configuration modal window.
 *
 * @param isOnAvailableStateEnabled Whether the corresponding setting is enabled.
 * @param setIsOnAvailableStateEnabled Setter for whether the corresponding setting is enabled.
 * @param onAvailableState Report property.
 * @param setOnAvailableState Setter for corresponding report property.
 * @param isOnBusyStateEnabled Whether the corresponding setting is enabled.
 * @param setIsOnBusyStateEnabled Setter for whether the corresponding setting is enabled.
 * @param onBusyState Report property.
 * @param setOnBusyState Setter for corresponding report property.
 * @param isOnNAOrDNDStateEnabled Whether the corresponding setting is enabled.
 * @param setIsOnNAOrDNDStateEnabled Setter for whether the corresponding setting is enabled.
 * @param onNAOrDNDState Report property.
 * @param setOnNAOrDNDState Setter for corresponding report property.
 * @param isOnWrapupStateEnabled Whether the corresponding setting is enabled.
 * @param setIsOnWrapupStateEnabled Setter for whether the corresponding setting is enabled.
 * @param onWrapupState Report property.
 * @param setOnWrapupState Setter for corresponding report property.
 */
const ACDStateThresholds: React.FC<Props> = ({
    isOnAvailableStateEnabled,
    setIsOnAvailableStateEnabled,
    onAvailableState,
    setOnAvailableState,
    isOnBusyStateEnabled,
    setIsOnBusyStateEnabled,
    onBusyState,
    setOnBusyState,
    isOnNAOrDNDStateEnabled,
    setIsOnNAOrDNDStateEnabled,
    onNAOrDNDState,
    setOnNAOrDNDState,
    isOnWrapupStateEnabled,
    setIsOnWrapupStateEnabled,
    onWrapupState,
    setOnWrapupState
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('ACDStateThresholds')

    return (
        <Fieldset className={'acd-state-thresholds'}>
            <Heading level={HeadingLevel.ONE}>
                {language.get(ACDStateThresholdsLanguage.ACDStateThresholds_SectionHeader)}
            </Heading>
            <P>{language.get(ACDStateThresholdsLanguage.ACDStateThresholds_SectionPara)}</P>
            <FormRow isInline className={'on-available-state'}>
                <Checkbox
                    labelLeft={language.get(ACDStateThresholdsLanguage.OnavailablestateEnabledHiddenlabel)}
                    isLabelHidden
                    onChange={setIsOnAvailableStateEnabled}
                    isChecked={isOnAvailableStateEnabled}
                />
                <TextInput
                    value={onAvailableState}
                    onChange={setOnAvailableState}
                    labelLeft={language.get(ACDStateThresholdsLanguage.OnavailablestateLabel)}
                    placeholder={language.get(ACDStateThresholdsLanguage.OnavailablestatePlaceholder)}
                    disabled={!isOnAvailableStateEnabled}
                />
            </FormRow>
            <FormRow isInline className={'on-busy-state'}>
                <Checkbox
                    labelLeft={language.get(ACDStateThresholdsLanguage.OnbusystateEnabledHiddenlabel)}
                    isLabelHidden
                    onChange={setIsOnBusyStateEnabled}
                    isChecked={isOnBusyStateEnabled}
                />
                <TextInput
                    value={onBusyState}
                    onChange={setOnBusyState}
                    labelLeft={language.get(ACDStateThresholdsLanguage.OnbusystateLabel)}
                    placeholder={language.get(ACDStateThresholdsLanguage.OnbusystatePlaceholder)}
                    disabled={!isOnBusyStateEnabled}
                />
            </FormRow>
            <FormRow isInline className={'on-na-or-dnd-state'}>
                <Checkbox
                    labelLeft={language.get(ACDStateThresholdsLanguage.OnnaordndstateEnabledHiddenlabel)}
                    isLabelHidden
                    onChange={setIsOnNAOrDNDStateEnabled}
                    isChecked={isOnNAOrDNDStateEnabled}
                />
                <TextInput
                    value={onNAOrDNDState}
                    onChange={setOnNAOrDNDState}
                    labelLeft={language.get(ACDStateThresholdsLanguage.OnnaordndstateLabel)}
                    placeholder={language.get(ACDStateThresholdsLanguage.OnnaordndstatePlaceholder)}
                    disabled={!isOnNAOrDNDStateEnabled}
                />
            </FormRow>
            <FormRow isInline className={'on-wrapup'}>
                <Checkbox
                    labelLeft={language.get(ACDStateThresholdsLanguage.OnwrapupEnabledHiddenlabel)}
                    isLabelHidden
                    onChange={setIsOnWrapupStateEnabled}
                    isChecked={isOnWrapupStateEnabled}
                />
                <TextInput
                    value={onWrapupState}
                    onChange={setOnWrapupState}
                    labelLeft={language.get(ACDStateThresholdsLanguage.OnwrapupLabel)}
                    placeholder={language.get(ACDStateThresholdsLanguage.OnwrapupPlaceholder)}
                    disabled={!isOnWrapupStateEnabled}
                />
            </FormRow>
        </Fieldset>
    )
}

export default React.memo(ACDStateThresholds)
